'use client'
import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import { Sizes } from '@/lib/constants/sizes'
import { typescale, typography, typographyVariantMapping } from './typography'
import { lightPalette } from './colors'

const theme = createTheme({
	breakpoints: { values: { xs: 0, sm: 600, md: 900, lg: 1280, xl: 1536 } },
	spacing: [0, 4, 8, 16, 24, 32, 40, 48, 64, 20, 10],
	shape: { borderRadius: 8 },
	palette: {
		...lightPalette,
		stroke: {
			...lightPalette.stroke,
			brand: {
				...lightPalette.stroke.brand,
				otherRevenue: lightPalette.stroke.brand.primary, // Add missing property with fallback value
			},
		},
	},
	typography: {
		...typography,
	},
	components: {
		MuiTypography: {
			defaultProps: {
				variantMapping: typographyVariantMapping,
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					backgroundColor: lightPalette.surface.neutral.t3Low, // was themeVars?.segmentedBackground
					borderRadius: '4px',
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					'textTransform': 'none',
					'color': lightPalette.foreground.neutral.onSurface, // was themeVars?.varBlack
					'borderRadius': '4px !important',
					'fontSize': typescale.extraSmall.fontSize,
					'lineHeight': typescale.extraSmall.lineHeight,
					'fontWeight': 500,
					'borderColor': lightPalette.surface.neutral.t3Low, // was themeVars?.segmentedBackground
					'&.Mui-selected': {
						backgroundColor: lightPalette.surface.neutral.t1Base, // was themeVars?.varWhite
						borderColor: lightPalette.surface.neutral.t3Low, // was themeVars?.segmentedBackground
					},
					'&.Mui-selected:hover': {
						backgroundColor: lightPalette.surface.neutral.t1Base, // was themeVars?.varWhite
						borderColor: lightPalette.surface.neutral.t3Low, // was themeVars?.segmentedBackground
					},
					'&:hover': {
						backgroundColor: lightPalette.surface.neutral.t1Base, // was themeVars?.varWhite
						borderColor: lightPalette.surface.neutral.t3Low, // was themeVars?.segmentedBackground
					},
					'@media (hover: none)': {
						'&:hover': {
							backgroundColor: 'inherit',
							borderColor: lightPalette.surface.neutral.t3Low, // was themeVars?.segmentedBackground
						},
					},
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				colorPrimary: {
					backgroundColor: lightPalette.surface.neutral.t1Base, // was themeVars?.varWhite
					color: lightPalette.foreground.neutral.onSurface, // was themeVars?.varBlack
				},
			},
		},
		MuiAvatar: {
			styleOverrides: {
				circular: {
					backgroundColor: lightPalette.surface.neutral.t4High, // was themeVars?.muiAvatarBackground - closest match
					fontWeight: 500,
					color: lightPalette.foreground.neutral.onSurface, // was themeVars?.varBlack
				},
			},
		},
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true,
			},
			styleOverrides: {
				root: {
					'&.MuiButtonBase-root': {
						'&.core-ui-input': {
							'&.Mui-selected': {
								'backgroundColor': lightPalette.surface.brand.primaryHighlight,
								'&:hover': {
									backgroundColor: lightPalette.surface.brand.primaryHighlight,
								},
							},
							'&.Mui-focusVisible': {
								outline: `2px solid ${lightPalette.stroke.neutral.focus}`,
								outlineOffset: '-4px',
							},
							'&.Mui-disabled': {
								opacity: 0.38,
							},
							'&.Mui-readOnly': {
								'backgroundColor': lightPalette.interaction.disabledOpacity,
								'borderColor': lightPalette.stroke.neutral.base,
								'&.Mui-focused': {
									borderColor: lightPalette.stroke.neutral.base,
									borderWidth: '1px',
								},
								'&:hover': {
									backgroundColor: lightPalette.interaction.disabledOpacity,
								},
								'&:active': {
									backgroundColor: lightPalette.interaction.disabledOpacity,
								},
							},
						},
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					'&.multi-select-textfield': {
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: lightPalette.stroke.neutral.focus,
							borderWidth: '2px',
						},
						'&.Mui-focused.open .MuiOutlinedInput-notchedOutline': {
							borderColor: lightPalette.primary.main,
							borderWidth: '1px',
						},
						'&:before': {
							border: 'none',
						},
						'&:hover:not(.Mui-disabled, .Mui-error):before': {
							border: 'none',
						},
						'&:focus .MuiSelect-nativeInput': {
							backgroundColor: 'transparent',
						},
						'&:hover': {
							'backgroundColor': lightPalette.interaction.neutralHoverOpacity,
							'border': 'none',
							'.MuiOutlinedInput-notchedOutline': {
								borderColor: lightPalette.stroke.neutral.base,
								borderWidth: '1px',
							},
						},
						'@media (hover: none)': {
							'&:hover': {
								color: lightPalette.secondary.main,
								backgroundColor: lightPalette.surface.neutral.transparent,
							},
						},
						'&:active': {
							backgroundColor: lightPalette.interaction.neutralPressOpacity,
						},
						'@media (max-width: 400px)': {
							whiteSpace: 'unset !important',
							overflow: 'unset !important',
						},
						'& .MuiInputBase-input': {
							color: lightPalette.foreground.neutral.onSurface,
							backgroundColor: lightPalette.surface.neutral.transparent,
						},
						'&.Mui-disabled': {
							color: `${lightPalette.secondary.main}`,
							backgroundColor: `${lightPalette.interaction.neutralHoverOpacity}`,
						},
						'&.Mui-disabled:before': {
							border: 'none !important',
						},
						'&.Mui-disabled:after': {
							border: 'none !important',
						},
						'& .MuiInputBase-root.Mui-disabled:before': {
							border: 'none ',
						},
						'& .MuiInputBase-root.Mui-disabled:after': {
							border: 'none',
						},
						'&:after': {
							border: 'none',
						},
						'.MuiOutlinedInput-notchedOutline': {
							borderColor: lightPalette.stroke.neutral.base,
						},
					},
				},
			},
		},
		MuiList: {
			styleOverrides: {
				root: {
					padding: 0,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					borderRadius: '4px',
				},
			},
			variants: [
				{
					props: {
						variant: 'bespoke',
						color: 'primary',
					},
					style: {
						'color': lightPalette.foreground.brand.onPrimary,
						'border': '1px solid',
						'borderColor': lightPalette.stroke.brand.primary,
						'background': `linear-gradient(${lightPalette.foreground.brand.primary}, ${lightPalette.foreground.brand.primary})`,
						'&:hover': {
							color: lightPalette.foreground.brand.onPrimary,
							border: '1px solid',
							borderColor: lightPalette.foreground.brand.primaryDepth,
							background: `linear-gradient(${lightPalette.interaction.brandHoverOpacity}, ${lightPalette.interaction.brandHoverOpacity}), linear-gradient(${lightPalette.foreground.brand.primary}, ${lightPalette.foreground.brand.primary})`,
						},
						'@media (hover: none)': {
							'&:hover': {
								color: lightPalette.foreground.brand.onPrimary,
								border: '1px solid',
								borderColor: lightPalette.stroke.brand.primary,
								background: `linear-gradient(${lightPalette.foreground.brand.primary}, ${lightPalette.foreground.brand.primary})`,
							},
						},
						'&:active': {
							color: lightPalette.foreground.brand.onPrimary,
							border: '1px solid',
							borderColor: lightPalette.foreground.brand.primaryDepth,
							background: `linear-gradient(${lightPalette.interaction.brandPressOpacity}, ${lightPalette.interaction.brandPressOpacity}), linear-gradient(${lightPalette.foreground.brand.primary}, ${lightPalette.foreground.brand.primary})`,
						},
						'&:focus-visible': {
							color: lightPalette.foreground.brand.onPrimary,
							border: '1px solid',
							borderColor: lightPalette.stroke.brand.primary,
							backgroundColor: lightPalette.foreground.brand.primary,
						},
						'&.Mui-disabled': {
							color: lightPalette.foreground.neutral.disabledOnSurface,
							borderColor: lightPalette.interaction.disabledOpacity,
							background: `linear-gradient(${lightPalette.interaction.disabledOpacity}, ${lightPalette.interaction.disabledOpacity})`,
						},
						'& .MuiButton-startIcon': {
							color: lightPalette.foreground.brand.onPrimary,
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						color: 'secondary',
					},
					style: {
						'color': lightPalette.foreground.neutral.onSurfaceVar01,
						'border': '1px solid',
						'borderColor': lightPalette.stroke.neutral.base,
						'backgroundColor': lightPalette.foreground.brand.onSecondary,
						'&:hover': {
							borderColor: lightPalette.stroke.neutral.base,
							backgroundColor: lightPalette.interaction.neutralHoverOpacity,
						},
						'@media (hover: none)': {
							'&:hover': {
								color: lightPalette.foreground.neutral.onSurfaceVar01,
								border: '1px solid',
								borderColor: lightPalette.stroke.neutral.base,
								backgroundColor: lightPalette.foreground.brand.onSecondary,
							},
						},
						'&:active': {
							borderColor: lightPalette.stroke.neutral.base,
							backgroundColor: lightPalette.interaction.neutralPressOpacity,
						},
						'&:focus-visible': {
							borderColor: lightPalette.stroke.neutral.focus,
							outline: `1px solid ${lightPalette.stroke.neutral.focus}`,
							backgroundColor: lightPalette.foreground.brand.onSecondary,
						},
						'&.Mui-disabled': {
							backgroundColor: lightPalette.interaction.disabledOpacity,
							color: lightPalette.foreground.neutral.disabledOnSurface,
						},
						'& .MuiButton-startIcon': {
							color: lightPalette.foreground.neutral.onSurfaceVar01,
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						color: 'tertiary',
					},
					style: {
						'color': lightPalette.foreground.neutral.onSurfaceVar01,
						'border': '1px solid',
						'borderColor': lightPalette.surface.neutral.transparent,
						'backgroundColor': lightPalette.surface.neutral.transparent,
						'&:hover': {
							borderColor: lightPalette.interaction.neutralHoverOpacity,
							backgroundColor: lightPalette.interaction.neutralHoverOpacity,
						},
						'@media (hover: none)': {
							'&:hover': {
								color: lightPalette.foreground.neutral.onSurfaceVar01,
								border: '1px solid',
								borderColor: lightPalette.surface.neutral.transparent,
								backgroundColor: lightPalette.surface.neutral.transparent,
							},
						},
						'&:active': {
							borderColor: lightPalette.interaction.neutralPressOpacity,
							backgroundColor: lightPalette.interaction.neutralPressOpacity,
						},
						'&:focus-visible': {
							borderColor: lightPalette.stroke.neutral.focus,
							outline: `1px solid ${lightPalette.stroke.neutral.focus}`,
							backgroundColor: lightPalette.surface.neutral.transparent,
						},
						'&.Mui-disabled': {
							backgroundColor: lightPalette.interaction.disabledOpacity,
							color: lightPalette.foreground.neutral.disabledOnSurface,
						},
						'& .MuiButton-startIcon': {
							color: lightPalette.foreground.neutral.onSurfaceVar01,
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						size: 'small',
					},
					style: {
						'height': '32px',
						'lineHeight': '32px',
						'fontSize': typescale.small.fontSize,
						'fontWeight': 500,
						'& .MuiButton-startIcon': {
							marginRight: '6px',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						size: 'medium',
					},
					style: {
						'height': '40px',
						'lineHeight': '40px',
						'fontSize': typescale.base.fontSize,
						'fontWeight': 500,
						'& .MuiButton-startIcon': {
							marginRight: '10px',
						},
					},
				},
				{
					props: {
						variant: 'bespoke',
						size: 'large',
					},
					style: {
						'height': '48px',
						'lineHeight': '48px',
						'fontSize': typescale.mediumMobile.fontSize,
						'fontWeight': 500,
						'& .MuiButton-startIcon': {
							marginRight: '12px',
						},
					},
				},
				{
					props: {
						variant: 'nav',
					},
					style: {
						'paddingTop': 3,
						'color': 'rgba(0, 0, 0, 0.16)',
						'backgroundColor': 'transparent',
						'borderRadius': 0,
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							width: 4,
							height: '100%',
							backgroundColor: 'transparent',
							clipPath: 'polygon(0 0, 100% 5%, 100% 95%, 0 100%)',
							transition: 'background-color 0.3s ease',
						},
						'&:hover::before': {
							backgroundColor: lightPalette.primary.main,
						},
						'&:hover': {
							backgroundColor: 'transparent',
						},
					},
				},
				{
					props: {
						variant: 'nav',
						size: 'expanded',
					},
					style: {
						height: '50px',
						flexDirection: 'row',
						justifyContent: 'flex-start',
					},
				},
				{
					props: {
						variant: 'nav',
						size: 'collapsed',
					},
					style: {
						height: '72px',
						flexDirection: 'column',
						justifyContent: 'flex-middle',
					},
				},
				{
					props: {
						variant: 'nav',
						color: 'selected',
					},
					style: {
						'color': lightPalette.primary.main,
						'&::before': {
							backgroundColor: lightPalette.primary.main,
						},
					},
				},
				{
					props: {
						variant: 'nav',
						color: 'default',
					},
					style: {
						'color': 'rgba(0, 0, 0, 0.54)',
						'&::before': {
							backgroundColor: 'transparent',
						},
					},
				},
			],
		},
		MuiAlert: {
			variants: [
				{
					props: {
						variant: 'outlined',
						severity: 'info',
						className: 'bannerNotification',
					},
					style: {
						'border': '2px solid #276ef1',
						'padding': 0,
						'overflow': 'hidden',
						'backgroundColor': lightPalette.foreground.brand.onPrimary,
						'& .MuiAlert-icon': {
							backgroundColor: '#276ef1',
							padding: '16px 12px',
						},
						'& .MuiSvgIcon-root.recruiting': {
							color: lightPalette.foreground.brand.onPrimary,
							width: 24,
							height: 24,
						},
						'& .MuiSvgIcon-root.bannerIcon-info': {
							color: lightPalette.foreground.brand.onPrimary,
						},
						'& .MuiAlert-message': {
							width: '100%',
						},
					},
				},
				{
					props: {
						variant: 'outlined',
						severity: 'warning',
						className: 'bannerNotification',
					},
					style: {
						'border': '2px solid #F6BC2F',
						'padding': 0,
						'overflow': 'hidden',
						'backgroundColor': lightPalette.foreground.brand.onPrimary,
						'& .MuiAlert-icon': {
							backgroundColor: '#F6BC2F',
							borderRadius: '0',
							padding: '16px 12px',
							marginRight: '12px',
						},
						'& .MuiAlert-icon .MuiSvgIcon-root': {
							color: lightPalette.warning.contrastText,
							width: 24,
							height: 24,
						},
						'& .MuiAlert-message': {
							width: '100%',
							padding: '8px 0 12px',
						},
					},
				},
				{
					props: {
						variant: 'outlined',
						severity: 'error',
						className: 'bannerNotification',
					},
					style: {
						'border': '2px solid #D83A52',
						'padding': 0,
						'overflow': 'hidden',
						'backgroundColor': lightPalette.foreground.brand.onPrimary,
						'& .MuiAlert-icon': {
							backgroundColor: '#D83A52',
							borderRadius: '0',
							padding: '16px 12px',
							marginRight: '12px',
						},
						'& .MuiAlert-icon .MuiSvgIcon-root': {
							color: lightPalette.foreground.brand.onPrimary,
							width: 24,
							height: 24,
						},
						'& .MuiAlert-message': {
							width: '100%',
							padding: '8px 0 12px',
						},
					},
				},
			],
		},
		MuiCard: {
			styleOverrides: {
				root: {
					'borderRadius': '.5em',
					'overflowY': 'auto',
					'.bi-listing-metrics-grid &': {
						'height': '100%',
						'display': 'flex',
						'padding': '12px',
						'flexDirection': 'column',
						'& > div': {
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
						},
						'& .MuiStack-root': {
							'height': '100%',
							'justifyContent': 'space-between',
							'& > :first-child': {
								marginBottom: 'auto',
							},
						},
					},
					'.overview-grid &': {
						'height': '100%',
						'display': 'flex',
						'padding': '12px',
						'flexDirection': 'column',
						'& > div': {
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
						},
						'& .MuiStack-root': {
							'height': '100%',
							'justifyContent': 'space-between',
							'& > :first-child': {
								'marginBottom': 'auto',
								'& .MuiBox-root': {
									'& > :first-child': {
										'@media (max-width: 600px)': {
											alignItems: 'baseline',
											minHeight: '36px',
										},
									},
								},
							},
						},
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					fontSize: typescale.extraSmall.fontSize,
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					backgroundColor: lightPalette.surface.neutral.t2Lowest, // was themeVars?.varDefaultBackgroundColor
				},
			},
		},
		MuiLink: {
			defaultProps: {
				underline: 'hover',
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				input: {
					padding: '4.5px 4px',
				},
				root: {
					padding: '0 10px',
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'&.core-ui-input': {
						'& .MuiOutlinedInput-root': {
							'&.Mui-disabled': {
								'backgroundColor': lightPalette.interaction.disabledOpacity,
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.stroke.neutral.base,
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.stroke.neutral.base, // Apply the desired border color
									borderWidth: '1px',
								},
							},
							'&.Mui-readOnly': {
								'backgroundColor': lightPalette.interaction.disabledOpacity,
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.stroke.neutral.base,
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.stroke.neutral.base, // Apply the desired border color
									borderWidth: '1px',
								},
								'&:hover': {
									backgroundColor: lightPalette.interaction.disabledOpacity,
								},
								'&:active': {
									backgroundColor: lightPalette.interaction.disabledOpacity,
								},
							},
							'backgroundColor': 'transparent',
							'& .MuiOutlinedInput-notchedOutline': {
								borderWidth: '1px',
							},
							'&:hover': {
								'@media (max-width: 600px)': {
									backgroundColor: 'transparent',
								},
								'&:not(.Mui-focused)': {
									backgroundColor: lightPalette.interaction.neutralHoverOpacity,
								},
							},
							'&:active': {
								'@media (max-width: 600px)': {
									backgroundColor: 'transparent',
								},
								'backgroundColor': lightPalette.interaction.neutralPressOpacity,
							},
							'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
								borderColor: lightPalette.stroke.brand.primary, // Apply the desired border color
								borderWidth: '2px', // Optional: Adjust border width
								backgroundColor: 'transparent',
							},
						},
					},
				},
			},
			variants: [
				{
					props: { color: 'primary' },
					style: {
						'&.core-ui-input': {
							'& .MuiOutlinedInput-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.stroke.neutral.base,
								},
							},
						},
					},
				},
				{
					props: { color: 'error' },
					style: {
						'&.core-ui-input': {
							'& .MuiOutlinedInput-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.error.main,
								},
							},
						},
					},
				},
			],
			defaultProps: {
				variant: 'outlined',
			},
		},
		MuiPaper: {
			defaultProps: {
				elevation: 0,
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					width: '100%',
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					'&:hover': {
						backgroundColor: lightPalette.surface.neutral.tableHover, // was themeVars?.muiHoverColor
						cursor: 'pointer',
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottom: '0.5px solid',
					borderBottomColor: lightPalette.surface.neutral.t2Lowest, // was themeVars?.varDefaultBackgroundColor
				},
				head: {
					'fontSize': 14,
					'fontStyle': 'normal',
					'fontWeight': typescale.weight.semiBold,
					'lineHeight': '24px',
					'letterSpacing': 0.18,
					'backgroundColor': lightPalette.surface.neutral.t1Base, // was themeVars?.varWhite
					'borderBottom': '0.5px solid',
					'borderBottomColor': lightPalette.surface.neutral.t2Lowest, // was themeVars?.varDefaultBackgroundColor
					'textAlign': 'left',
					'textTransform': 'capitalize',
					'padding': '10px',
					'&:first-of-type': {
						'paddingLeft': '10px',
						'@media (max-width: 400px)': {
							paddingLeft: '8px',
						},
					},
					'&:last-of-type': {
						'paddingRight': '16px',
						'@media (max-width: 400px)': {
							paddingRight: '10px',
						},
					},
				},
				body: {
					'fontSize': 14,
					'lineHeight': 1.5,
					'fontWeight': typescale.weight.regular,
					'letterSpacing': '0.17px',
					'textAlign': 'left',
					'padding': '10px',
					'&:first-of-type': {
						'paddingLeft': '10px',
						'@media (max-width: 400px)': {
							paddingLeft: '10px',
						},
					},
					'&:last-of-type': {
						'paddingRight': '16px',
						'@media (max-width: 400px)': {
							paddingRight: '10px',
						},
					},
				},
			},
		},
		MuiToolbar: {
			styleOverrides: {
				root: {
					minHeight: Sizes.TOP_NAV_HEIGHT,
				},
			},
		},
		MuiTableSortLabel: {
			styleOverrides: {
				root: {
					'&:hover': {
						'color': lightPalette.foreground.neutral.onSurface, // was themeVars?.varBlack
						'& .MuiTableSortLabel-icon': {
							backgroundColor: lightPalette.interaction.neutralHoverOpacity, // was themeVars?.selectedLight
							borderRadius: '48px',
							opacity: '100',
						},
					},
					'&:active': {
						'& .MuiTableSortLabel-icon': {
							backgroundColor: lightPalette.interaction.neutralPressOpacity, // was themeVars?.selectedDark
							borderRadius: '48px',
							opacity: '100',
						},
					},
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						'backgroundColor': lightPalette.interaction.neutralPressOpacity, // was themeVars?.selectedDark
						'&:hover': {
							backgroundColor: lightPalette.interaction.neutralHoverOpacity, // was themeVars?.selectedLight
						},
					},
					'&:hover': {
						backgroundColor: lightPalette.interaction.neutralHoverOpacity, // was themeVars?.selectedLight
					},
				},
			},
		},
		MuiStepper: {
			styleOverrides: {
				root: {
					'& .Mui-completed': {
						'&.MuiStepIcon-root': {
							fontSize: '2em',
							fill: 'success.main',
							borderColor: 'success.main',
							border: '0px solid',
						},
					},
				},
			},
		},
		MuiStepLabel: {
			styleOverrides: {
				root: {
					'.MuiStepLabel-label': {
						color: lightPalette.foreground.neutral.onSurface, // was themeVars?.onSurface
					},
				},
			},
		},
		MuiStepIcon: {
			styleOverrides: {
				root: {
					'fontSize': '2.5em',
					'border': '2px solid',
					'borderRadius': '50%',
					'borderColor': lightPalette.stroke.neutral.base, // was themeVars?.muiStepIconBorder
					'fill': 'transparent',
					'.MuiStepIcon-text': {
						fill: lightPalette.foreground.neutral.onSurface, // was themeVars?.onSurface
						fontWeight: '600',
					},
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						backgroundColor: 'transparent',
					},
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						opacity: '1',
					},
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					'backgroundColor': lightPalette.surface.neutral.t1Base,
					'minHeight': 'unset',
					'&.core-ui-tabs ': {
						'.MuiTab-root': {
							'fontSize': typescale.small.fontSize,
							'&:hover': {
								'backgroundColor': lightPalette.interaction.neutralHoverOpacity,
								'@media (hover: none)': {
									backgroundColor: 'transparent',
								},
							},
							'&:active': {
								backgroundColor: lightPalette.interaction.brandPressOpacity, // or any color you want
							},
							'&:focus-visible': {
								outline: `2px solid ${lightPalette.stroke.neutral.focus}`, // Use your dark color here
								outlineOffset: '-2px',
							},
							'.Mui-selected': {
								color: lightPalette.foreground.brand.primary,
							},
							'.Mui-disabled': {
								color: lightPalette.foreground.neutral.disabledOnSurface,
							},
							'.MuiTabs-fixed:has(.Mui-disabled.Mui-selected) .MuiTabs-indicator > span': {
								backgroundColor: lightPalette.foreground.neutral.disabledOnSurface,
							},
							'@media (max-width: 600px)': {
								fontSize: typescale.base.fontSize,
							},
							'@media (max-width: 450px)': {
								minWidth: 'min-content',
								textAlign: 'left',
							},
						},
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: '400',
					fontSize: '14px',
					lineHeight: '20px',
					minWidth: 'fit-content',
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					'&:before': {
						border: 'none',
					},
					'&:hover:not(.Mui-disabled, .Mui-error):before': {
						border: 'none',
					},
					'&:focus .MuiSelect-nativeInput': {
						border: 'none',
						backgroundColor: 'transparent',
					},
					'& .MuiSelect-icon': {
						fontSize: 20,
						color: lightPalette.surface.neutral.t5Highest,
					},
					'& .MuiInputBase-input': {
						'padding': '0 !important',
						'&:focus': {
							backgroundColor: 'transparent',
							borderRadius: '4px',
						},
						'@media (max-width: 400px)': {
							whiteSpace: 'unset !important',
							overflow: 'unset !important',
						},
					},
					'&:after': {
						border: 'none',
					},
				},
			},
			variants: [
				{
					props: { color: 'error' },
					style: {
						'&.core-ui-input': {
							'& .MuiOutlinedInput-root': {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: lightPalette.error.main,
								},
							},
						},
					},
				},
			],
		},
		MuiSwitch: {
			styleOverrides: {
				root: {
					'width': 36,
					'height': 20,
					'padding': 0,
					'& .MuiSwitch-switchBase': {
						'padding': 0,
						'margin': 2,
						'transitionDuration': '100ms',
						'&.Mui-checked': {
							'transform': 'translate(15px, -0.2px)',
							'color': lightPalette.foreground.neutral.onColor,
							'& + .MuiSwitch-track': {
								backgroundColor: 'primary',
								opacity: 1,
								border: 0,
							},
							'&.Mui-disabled .MuiSwitch-thumb': {
								backgroundColor: lightPalette.foreground.neutral.disabledOnColorSurface,
							},
							'&.Mui-disabled': {
								transform: 'translate(15px, -0.2px)',
							},
						},
						'&.Mui-disabled .MuiSwitch-thumb': {
							backgroundColor: lightPalette.foreground.neutral.disabledOnSurface,
						},
						'&.Mui-disabled + .MuiSwitch-track': {
							backgroundColor: lightPalette.interaction.disabledOpacity,
							opacity: 1,
						},
						'&.Mui-disabled': {
							transform: 'translate(1px, -0.8px)',
						},
					},
					'& .MuiSwitch-thumb': {
						boxSizing: 'border-box',
						width: 16,
						height: 16,
					},
					'& .MuiSwitch-track': {
						borderRadius: '16px',
						backgroundColor: lightPalette.surface.neutral.t4High,
						opacity: 1,
					},
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					'& .MuiRadioGroup-root': {
						'& .MuiRadio-root': {
							'padding': '0 !important',
							'color': lightPalette.stroke.neutral.base,
							'&.Mui-checked': {
								color: lightPalette.stroke.brand.primary,
							},
							'&.Mui-disabled': {
								color: lightPalette.stroke.neutral.disabledLight,
							},
						},
						'& .MuiFormControlLabel-root': {
							'flexDirection': 'row-reverse',
							'justifyContent': 'space-between',
							'margin': 0,
							'&:focus-visible': {
								outline: `2px solid ${lightPalette.stroke.neutral.focus}`,
								zIndex: 1,
							},
							'@media (hover: none)': {
								'&:active': {
									backgroundColor: lightPalette.interaction.neutralHoverOpacity,
								},
							},
							'@media (hover: hover)': {
								'&:hover': {
									backgroundColor: lightPalette.interaction.neutralHoverOpacity,
								},
							},
						},
						'& .MuiFormControlLabel-label': {
							'color': lightPalette.foreground.neutral.onSurface,
							'fontSize': typescale.base.fontSize,
							'lineHeight': typescale.base.lineHeight,
							'fontWeight': 500,
							'&.Mui-disabled': {
								color: lightPalette.foreground.neutral.disabledOnSurface,
							},
						},
					},
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: 0,
					color: lightPalette.stroke.neutral.base,
				},
			},
		},
		MuiStack: {
			styleOverrides: {
				root: {
					'&.metric-card-wrapper': {
						'& .MuiCard-root': {
							'padding': 12,
							'paddingBottom': 28,
							'@media (max-width: 900px)': {
								padding: 12,
							},
						},
					},
					'&.bi-listings-header': {
						'& .MuiAlert-icon': {
							marginRight: '12px',
						},
						'& .MuiAlert-message': {
							'marginLeft': 0,
							'marginRight': 0,
							'@media (max-width: 900px)': {
								marginLeft: 0,
								marginRight: 0,
							},
						},
					},
					'&.engagement-header': {
						'& .MuiAlert-icon': {
							marginRight: '12px',
							padding: '18px 12px',
						},
						'& .MuiAlert-message': {
							'marginLeft': 0,
							'marginRight': 0,
							'@media (max-width: 900px)': {
								marginLeft: 0,
								marginRight: 0,
							},
							'& .MuiStack-root': {
								margin: '14px 0',
							},
							'& .MuiBox-root': {
								marginBottom: '8px',
							},
						},
					},
					'&.listing-segmented-controller': {
						'& .MuiToggleButtonGroup-root': {
							'padding': 2,
							'borderRadius': 4,
							'gap': 0,
							'width': 'auto',
							'@media (max-width: 900px)': {
								width: '100%',
							},
							'@media (max-width: 1279px)': {
								width: '100%',
							},
							'& .MuiToggleButtonGroup-grouped': {
								'paddingTop': 8,
								'paddingBottom': 8,
								'@media (max-width: 1279px)': {
									width: '50%',
								},
								'& .MuiTypography-root': {
									fontWeight: 500,
									fontSize: typescale.base.fontSize,
									lineHeight: typescale.base.lineHeight,
								},
							},
						},
					},
				},
			},
		},
	},
})
const lightTheme = responsiveFontSizes(theme)

export default lightTheme
